import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Hash } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { vessels, destinations } from "../data/vessels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ preselectedVessel, preselectedDestination }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", vessel: "", destination: "", dates: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preselectedVessel) setForm((f) => ({ ...f, vessel: preselectedVessel }));
  }, [preselectedVessel]);

  useEffect(() => {
    if (preselectedDestination) setForm((f) => ({ ...f, destination: preselectedDestination }));
  }, [preselectedDestination]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", vessel: "", destination: "", dates: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: MapPin, label: t.contact.details.addressLabel, value: "Ulica Maka Dizdara 14, 44000 Sisak, Hrvatska", testid: "contact-address" },
    { icon: Hash, label: t.contact.details.oibLabel, value: "02179948320", testid: "contact-oib" },
    { icon: Mail, label: t.contact.details.emailLabel, value: "info@malta-adria.eu", href: "mailto:info@malta-adria.eu", testid: "contact-email" },
    { icon: Phone, label: t.contact.details.phoneLabel, value: "+356 7963 7742", href: "https://wa.me/35679637742", testid: "contact-phone" },
  ];

  return (
    <section id="contact" className="relative py-24 sm:py-36 bg-[#0B1528]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-2"
        >
          <p className="font-label text-[10px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-5" data-testid="contact-overline">
            {t.contact.overline}
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F7F5F0] leading-[1.05]" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-sm sm:text-base text-slate-400 font-light leading-relaxed max-w-md">{t.contact.sub}</p>

          <div className="mt-12 border border-[rgba(212,175,55,0.2)] p-8 bg-[#050B14]/60">
            <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">{t.contact.details.title}</p>
            <p className="font-display text-2xl text-[#F7F5F0] font-light mb-8">DLD TURIZAM j.d.o.o.</p>
            <div className="space-y-5">
              {details.map(({ icon: Icon, label, value, href, testid }) => (
                <div key={label} className="flex items-start gap-4" data-testid={testid}>
                  <Icon size={16} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-label text-[9px] tracking-[0.3em] uppercase text-slate-500 mb-1">{label}</p>
                    {href ? (
                      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm text-[#F7F5F0] hover:text-[#D4AF37] transition-colors duration-300">
                        {value}
                      </a>
                    ) : (
                      <p className="text-sm text-[#F7F5F0]">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8"
          data-testid="enquiry-form"
        >
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.name} *</label>
            <input required value={form.name} onChange={set("name")} className="lux-input" data-testid="enquiry-name-input" />
          </div>
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.email} *</label>
            <input required type="email" value={form.email} onChange={set("email")} className="lux-input" data-testid="enquiry-email-input" />
          </div>
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.phone}</label>
            <input value={form.phone} onChange={set("phone")} className="lux-input" data-testid="enquiry-phone-input" />
          </div>
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.dates}</label>
            <input value={form.dates} onChange={set("dates")} className="lux-input" data-testid="enquiry-dates-input" />
          </div>
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.vessel}</label>
            <select value={form.vessel} onChange={set("vessel")} className="lux-input bg-[#0B1528]" data-testid="enquiry-vessel-select">
              <option value="">{t.contact.form.vesselAny}</option>
              {vessels.map((v) => (
                <option key={v.id} value={v.name}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.destination}</label>
            <select value={form.destination} onChange={set("destination")} className="lux-input bg-[#0B1528]" data-testid="enquiry-destination-select">
              <option value="">{t.contact.form.destinationAny}</option>
              {destinations.map((d) => (
                <option key={d.id} value={t.destinations.items[d.id].name}>{t.destinations.items[d.id].name}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">{t.contact.form.message} *</label>
            <textarea required rows={4} value={form.message} onChange={set("message")} className="lux-input resize-none" data-testid="enquiry-message-input" />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="font-label text-[11px] tracking-[0.3em] uppercase px-12 py-4 bg-[#D4AF37] text-[#050B14] font-semibold hover:bg-[#F3E5AB] disabled:opacity-50 transition-colors duration-300"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
